/**
 * Available Moves - State Observer
 *
 * Pure functions that determine which moves are currently available.
 * Used by both game moves (for validation) and UI (for hints/enabling controls).
 */

import { getCardValue, getCumulativeValue, handHasAce } from './cardUtils.js';

// ============================================================================
// Drawing Validators
// ============================================================================

/**
 * Check if player can draw cards (2 normally, 3 on priority turn).
 * Allowed at start of turn if not unstashed.
 */
export function canDraw2Cards(G, playerID) {
  if (G.setupPhase !== 'play') return false;
  const player = G.players[playerID];
  const turn = G.turnState;

  // Can't draw if already drew OR if unstashed this turn
  if (turn.hasDrawn || turn.hasUnstashed) return false;

  // Determine draw count based on priority turn
  const drawCount = turn.isPriorityTurn ? 3 : 2;
  return player.stack.length >= drawCount;
}

/**
 * Check if player can draw card(s) after unstashing.
 * Only allowed after unstashing.
 * Draws 1 normally, 2 on priority turn.
 */
export function canDraw1Card(G, playerID) {
  if (G.setupPhase !== 'play') return false;
  const player = G.players[playerID];
  const turn = G.turnState;

  // Can only draw after unstashing
  if (turn.hasDrawn || !turn.hasUnstashed) return false;

  // Determine draw count based on priority turn
  const drawCount = turn.isPriorityTurn ? 2 : 1;
  return player.stack.length >= drawCount;
}

/**
 * Check if player can draw a bonus card (after exposing opponent's general).
 */
export function canDrawBonusCard(G, playerID) {
  if (G.setupPhase !== 'play') return false;
  const player = G.players[playerID];

  return G.turnState.bonusDrawAllowed && player.stack.length >= 1;
}

// ============================================================================
// Stash Validators
// ============================================================================

/**
 * Check if player can stash a card.
 */
export function canStashCard(G, playerID) {
  if (G.setupPhase !== 'play') return false;
  const player = G.players[playerID];

  return player.stash === null && player.hand.length > 0;
}

/**
 * Check if player can unstash (reveal stashed card).
 * Can only happen at start of turn, before drawing.
 */
export function canUnstashCard(G, playerID) {
  if (G.setupPhase !== 'play') return false;
  const player = G.players[playerID];
  const turn = G.turnState;

  // Can only unstash at start of turn (before drawing)
  return player.stash !== null && !turn.hasUnstashed && !turn.hasDrawn;
}

// ============================================================================
// Defender Validators
// ============================================================================

/**
 * Check if player can create a defender (place card in empty slot).
 */
export function canCreateDefender(G, playerID) {
  if (G.setupPhase !== 'play') return false;
  const player = G.players[playerID];

  if (player.hand.length === 0) return false;

  // At least one defender slot must be empty (null means empty)
  return !player.defender1 || !player.defender2;
}

/**
 * Get empty defender slots for current player.
 * @returns {number[]} Array of available slot numbers (1 and/or 2)
 */
export function getEmptyDefenderSlots(G, playerID) {
  const player = G.players[playerID];
  const slots = [];
  if (!player.defender1) slots.push(1);
  if (!player.defender2) slots.push(2);
  return slots;
}

// ============================================================================
// Attack Validators
// ============================================================================

/**
 * Check if a specific opponent defender can be attacked.
 */
export function canAttackDefender(G, playerID, targetPlayerId, slot) {
  if (G.setupPhase !== 'play') return false;
  const player = G.players[playerID];
  const target = G.players[targetPlayerId];

  if (!target || playerID === targetPlayerId) return false;
  if (player.hand.length === 0) return false;

  // Defender is now an object { card, attacks } or null
  const defender = slot === 2 ? target.defender2 : target.defender1;
  return defender !== null && defender.card !== null;
}

/**
 * Get all attackable defender positions.
 * @returns {Array<{playerId: string, slot: number}>}
 */
export function getAttackableDefenders(G, playerID) {
  if (G.setupPhase !== 'play') return [];
  const player = G.players[playerID];
  if (player.hand.length === 0) return [];

  const targets = [];
  for (const [oppId, opponent] of Object.entries(G.players)) {
    if (oppId === playerID) continue;

    // Check defender objects exist and have cards
    if (opponent.defender1 && opponent.defender1.card) {
      targets.push({ playerId: oppId, slot: 1 });
    }
    if (opponent.defender2 && opponent.defender2.card) {
      targets.push({ playerId: oppId, slot: 2 });
    }
  }
  return targets;
}

/**
 * Get all defenders that can be replaced with an Ace.
 * Includes both own defenders (for swapping) and opponent defenders (for attacking).
 * @returns {Array<{playerId: string, slot: number}>}
 */
export function getReplaceableDefendersWithAce(G, playerID) {
  if (G.setupPhase !== 'play') return [];
  const player = G.players[playerID];
  if (!handHasAce(player.hand)) return [];

  const targets = [];
  for (const [playerId, p] of Object.entries(G.players)) {
    // Include ALL players (self and opponents)
    if (p.defender1 && p.defender1.card) {
      targets.push({ playerId, slot: 1 });
    }
    if (p.defender2 && p.defender2.card) {
      targets.push({ playerId, slot: 2 });
    }
  }
  return targets;
}

/**
 * Check if a specific opponent's general can be attacked.
 * General can only be attacked if opponent has no defenders.
 */
export function canAttackGeneral(G, playerID, targetPlayerId) {
  if (G.setupPhase !== 'play') return false;
  const player = G.players[playerID];
  const target = G.players[targetPlayerId];

  if (!target || playerID === targetPlayerId) return false;
  if (player.hand.length === 0) return false;
  // General is now an object { card, attacks } - check if player is eliminated
  if (!target.general || !target.general.card) return false;

  // Can only attack general if target has no defenders
  return !target.defender1 && !target.defender2;
}

/**
 * Get all attackable generals (opponents with no defenders).
 * In bonus mode, only the bonusTarget is attackable.
 * @returns {Array<{playerId: string}>}
 */
export function getAttackableGenerals(G, playerID) {
  if (G.setupPhase !== 'play') return [];
  const player = G.players[playerID];
  if (player.hand.length === 0) return [];

  const targets = [];
  for (const [oppId, opponent] of Object.entries(G.players)) {
    if (oppId === playerID) continue;
    // General is now an object - check if player is eliminated
    if (!opponent.general || !opponent.general.card) continue;

    // In bonus mode, can only attack the bonus target
    if (G.turnState.bonusTarget && G.turnState.bonusTarget !== oppId) {
      continue;
    }

    // Can only attack general if no defenders
    if (!opponent.defender1 && !opponent.defender2) {
      targets.push({ playerId: oppId });
    }
  }
  return targets;
}

/**
 * Check if player can use ace replacement on any defender.
 * This includes own defenders (for swapping) and opponent defenders (for attacking).
 */
export function canReplaceDefenderWithAce(G, playerID) {
  if (G.setupPhase !== 'play') return false;
  const player = G.players[playerID];

  if (!handHasAce(player.hand)) return false;

  // Must have a target defender to replace (own or opponent)
  return getReplaceableDefendersWithAce(G, playerID).length > 0;
}

/**
 * Check if player can use ace to replace their own general (self-saving move).
 * This clears accumulated attacks on the general.
 */
export function canReplaceOwnGeneralWithAce(G, playerID) {
  if (G.setupPhase !== 'play') return false;
  const player = G.players[playerID];

  // Must have an ace in hand
  if (!handHasAce(player.hand)) return false;

  // Must have a general to replace
  return player.general && player.general.card !== null;
}

// ============================================================================
// Turn Control Validators
// ============================================================================

/**
 * Check if player can end their turn.
 * Can only end when hand is empty AND player has made at least one move.
 */
export function canEndTurn(G, playerID) {
  if (G.setupPhase !== 'play') return false;
  const player = G.players[playerID];

  // Must have made at least one move AND have empty hand AND no pending bonus draws
  return G.turnState.hasMadeFirstMove && player.hand.length === 0 && !canDrawBonusCard(G, playerID);
}

// ============================================================================
// Main Observer Function
// ============================================================================

/**
 * Get all available moves for a player.
 * Returns an array of move objects with type and optional target info.
 *
 * @param {object} G - Game state
 * @param {object} ctx - Context with currentPlayer
 * @param {string} playerID - The player to check moves for
 * @returns {Array<{type: string, ...}>} Available moves
 */
export function getAvailableMoves(G, ctx, playerID) {
  const moves = [];

  // Only check moves for current player during play phase
  if (ctx.currentPlayer !== playerID) return moves;
  if (G.setupPhase !== 'play') return moves;

  // Bonus draw (after exposing general with last card)
  if (canDrawBonusCard(G, playerID)) {
    moves.push({ type: 'drawBonusCard' });
  }

  // Start of turn choices (mutually exclusive with each other)
  if (canDraw2Cards(G, playerID)) {
    moves.push({ type: 'draw2Cards' });
  }
  if (canUnstashCard(G, playerID)) {
    moves.push({ type: 'unstashCard' });
  }
  if (canDraw1Card(G, playerID)) {
    moves.push({ type: 'draw1Card' });
  }

  // Defender creation
  if (canCreateDefender(G, playerID)) {
    moves.push({
      type: 'createDefender',
      slots: getEmptyDefenderSlots(G, playerID),
    });
  }

  // Attack moves
  const defenderTargets = getAttackableDefenders(G, playerID);
  if (defenderTargets.length > 0) {
    moves.push({
      type: 'attackDefender',
      targets: defenderTargets,
    });
  }

  const generalTargets = getAttackableGenerals(G, playerID);
  if (generalTargets.length > 0) {
    moves.push({
      type: 'attackGeneral',
      targets: generalTargets,
    });
  }

  // Ace replacement on defenders (includes own and opponent defenders)
  const aceDefenderTargets = getReplaceableDefendersWithAce(G, playerID);
  if (aceDefenderTargets.length > 0) {
    moves.push({
      type: 'replaceDefenderWithAce',
      targets: aceDefenderTargets,
    });
  }

  // Ace replacement on own general (self-saving)
  if (canReplaceOwnGeneralWithAce(G, playerID)) {
    moves.push({ type: 'replaceOwnGeneralWithAce' });
  }

  // Stash
  if (canStashCard(G, playerID)) {
    moves.push({ type: 'stashCard' });
  }

  // End turn
  if (canEndTurn(G, playerID)) {
    moves.push({ type: 'endTurn' });
  }

  return moves;
}

/**
 * Helper to check if a specific move type is available.
 */
export function hasMoveAvailable(G, ctx, playerID, moveType) {
  const moves = getAvailableMoves(G, ctx, playerID);
  return moves.some(m => m.type === moveType);
}
